import { uploadInstrumentOptionImage } from '@/features/instruments/infra/instrumentDraft.api'
import { ApiError } from '@/api/http/types'
import { env } from '@/config/env'

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

const UPLOAD_UNAVAILABLE_MESSAGE =
  'Image upload is temporarily unavailable. Please try again later or contact your administrator.'

export function pictureChoiceMaxFileHumanMb(): string {
  const mb = env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES / (1024 * 1024)
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(1)
}

/** Hide infra/config dump from Sensemakers when storage is misconfigured. */
export function mapOptionImageUploadError(err: unknown): Error {
  if (err instanceof ApiError) {
    if (
      err.status === 503 ||
      /S3_|MinIO|Object storage|not configured for image/i.test(err.message)
    ) {
      return new Error(UPLOAD_UNAVAILABLE_MESSAGE)
    }
    return err
  }
  if (err instanceof Error) {
    if (/S3_|MinIO|Object storage|not configured for image/i.test(err.message)) {
      return new Error(UPLOAD_UNAVAILABLE_MESSAGE)
    }
    return err
  }
  return new Error('Image upload failed')
}

export async function uploadBuilderOptionImage(params: {
  /** Optional — retained for call-site context; upload is instrument-scoped. */
  campaignId?: string
  instrumentId: string
  optionId: string
  file: File
}): Promise<string> {
  if (!params.instrumentId) {
    throw new Error('Instrument is required to upload images')
  }
  if (!ALLOWED_TYPES.has(params.file.type)) {
    throw new Error('Image must be JPEG, PNG, or WEBP')
  }
  if (params.file.size > env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES) {
    throw new Error(
      `Each image must be ${pictureChoiceMaxFileHumanMb()} MB or less (JPG, PNG, or WEBP).`,
    )
  }
  try {
    const { imageUrl } = await uploadInstrumentOptionImage({
      campaignId: params.campaignId,
      instrumentId: params.instrumentId,
      optionId: params.optionId,
      file: params.file,
    })
    return imageUrl
  } catch (err: unknown) {
    throw mapOptionImageUploadError(err)
  }
}
